import React, { useState, useEffect } from 'react';
import { complaintService } from '../../services/complaint/complaintService';
import { Complaint } from '../../types/complaint';
import { TriageModal } from '../../components/workflow/TriageModal';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusBadge, PriorityBadge } from '../../components/ui/Badge';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { ShieldCheck, UserCheck, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';

export const OfficerWorkspacePage: React.FC<{ onSelectComplaint: (c: Complaint) => void }> = ({ onSelectComplaint }) => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTriage, setSelectedTriage] = useState<Complaint | null>(null);

  const loadTriageQueue = async () => {
    setIsLoading(true);
    const res = await complaintService.getComplaints();
    if (res.success) {
      setComplaints(res.data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadTriageQueue();
  }, []);

  const unassignedQueue = complaints.filter(c => c.status_name === 'New' || c.status_name === 'Under Review');
  const assignedQueue = complaints.filter(c => c.status_name === 'Assigned' || c.status_name === 'In Progress');

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div>
        <h2 className="text-xl font-semibold text-slate-100">Officer Workspace & Triage Queue</h2>
        <p className="text-xs text-slate-400">Review incoming complaints, set priorities, assign field supervisors, and manage escalations</p>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="flex items-center gap-3">
          <div className="p-3 bg-amber-500/10 text-amber-400 rounded-xl">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Pending Triage Review</p>
            <p className="text-2xl font-semibold text-slate-100">{unassignedQueue.length}</p>
          </div>
        </Card>

        <Card className="flex items-center gap-3">
          <div className="p-3 bg-sky-500/10 text-sky-400 rounded-xl">
            <UserCheck className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Active Supervisor Assignments</p>
            <p className="text-2xl font-semibold text-slate-100">{assignedQueue.length}</p>
          </div>
        </Card>

        <Card className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-xl">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-slate-400">Triage SLA Health</p>
            <p className="text-2xl font-semibold text-emerald-400">100% On-Time</p>
          </div>
        </Card>
      </div>

      {/* Unassigned Triage Queue */}
      <Card title="Incoming Unassigned Complaints (Triage Required)" subtitle="Select complaint to assign supervisor and confirm priority">
        {isLoading ? (
          <LoadingSpinner size="md" text="Loading Triage Queue..." />
        ) : unassignedQueue.length === 0 ? (
          <p className="text-xs text-slate-400 p-4 text-center">No pending complaints requiring triage.</p>
        ) : (
          <div className="divide-y divide-slate-800">
            {unassignedQueue.map(c => (
              <div key={c.complaint_id} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs text-sky-400 font-semibold">{c.complaint_id}</span>
                    <PriorityBadge priority={c.priority_name} />
                    <StatusBadge status={c.status_name} />
                  </div>
                  <h4 className="font-semibold text-slate-100 text-sm">{c.title}</h4>
                  <p className="text-xs text-slate-400 mt-0.5">{c.department_name} â€¢ {c.location_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => onSelectComplaint(c)}>Details</Button>
                  <Button size="sm" variant="primary" icon={<ArrowRight className="w-3.5 h-3.5" />} onClick={() => setSelectedTriage(c)}>
                    Triage & Assign
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Triage Modal */}
      <TriageModal
        isOpen={!!selectedTriage}
        onClose={() => setSelectedTriage(null)}
        complaint={selectedTriage}
        onSuccess={loadTriageQueue}
      />
    </div>
  );
};
